import { JSX } from "react";
import "../../../../App.scss";
import JoditEditor from "jodit-react";
import { appContext } from "../../../../AppContext";
import { IbssPage } from "../../../../Components/Core/BasePage/IbssPage";
import { IRouterProps, withRouting } from "@iconics/components";
import { SerendieSymbol } from "@serendie/symbols";
import { Switch } from "@serendie/ui";
import { Validator } from '@iconics/core';
import { NotificationData } from "./DataModels";
import { IPostNotificationRule, IPutNotificationRule, NotificationAreaEnum, NotificationRuleAttachmentTypeEnum } from '@iconics/providers.fadx-platform-api-client-v2';
import { Button, ButtonBar, Card, DeleteButton, GridContainer, GridItem, HelperText, IPageNavSection, LayoutContainer, LayoutContent, LayoutFooter, LayoutRight, LeftButtons, PageNav, RightButtons, Select, TextArea, TextField } from "@iconics/components";

class EditNotification extends IbssPage<IProps, IState>
{
    private validator = new Validator(appContext().core.validationOptions);
    private get labels() { return appContext().labels; }

    private userCanUpdate: boolean;
    private userCanDelete: boolean;

    private notificationAreas: { label: string, value: NotificationAreaEnum }[] = [
        { label: this.labels.Notification_NotificationArea_Generic, value: 'Generic' },
        { label: this.labels.Notification_NotificationArea_IBSS_Visitors, value: 'IBSS_Visitors' },
        { label: this.labels.Notification_NotificationArea_IBSS_Bookings, value: 'IBSS_Bookings' },
        { label: this.labels.Notification_NotificationArea_IBSS_Tasks, value: 'IBSS_Tasks' },
    ];

    private get isNew(): boolean
    {
        return this.notificationId === 0;
    }

    private get notificationId(): number
    {
        return parseInt(this.props.params.notificationid ?? '');
    }

    constructor(props: IProps)
    {
        super(props);
        this.state = {
            name: '',
            nameError: '',
            eventTypeName: '',
            eventTypeError: '',
            ruleExpression: '',
            ruleExpressionError: '',
            disabled: false,
            shouldSendMail: false,
            shouldSendMobilePush: false,

            to: '',
            toError: '',
            replyTo: '',
            cc: '',
            bcc: '',
            subject: '',
            subjectError: '',
            isHtml: false,
            message: '',
            messageError: '',

            mobilePushTo: '',
            mobilePushToError: '',
            mobilePushTitle: '',
            mobilePushTitleError: '',
            mobilePushMessage: '',
            mobilePushMessageError: '',
            notificationArea: '',
            notificationAreaLabel: '',
            notificationAreaError: '',
            mobilePushBadge: '',
            mobilePushBadgeError: '',
            mobilePushAction: '',
            mobilePushActionError: '',
            mobilePushActionUri: '',
            mobilePushActionUriError: '',

            attachmentType: '',
            shouldSendTwilio: false,
            twilioTo: '',
            twilioMessage: '',
            concurrencyTimestamp: '',

            sections: [],
        };

        this.userCanUpdate = appContext().localStorageProvider.hasRight('ADMINPORTAL.Notifications.Update');
        this.userCanDelete = appContext().localStorageProvider.hasRight('ADMINPORTAL.Notifications.Delete');
    }

    public async componentDidMount(): Promise<void>
    {
        this.pageTitle = this.labels.Platform_Notification_PageTitle;
        this.backUrl = '/notifications';
        if (!this.isNew)
        {
            await this.loading(async () => this.loadNotification());
        }
        this.addValidatorSettings();
    }

    private addValidatorSettings(): void
    {
        this.validator
            .addInput('name', () => this.state.name, i => this.setState({ nameError: i }))
            .isRequired();
        this.validator
            .addInput('eventTypeName', () => this.state.eventTypeName, i => this.setState({ eventTypeError: i }))
            .isRequired();
        this.validator
            .addInput('ruleExpression', () => this.state.ruleExpression, i => this.setState({ ruleExpressionError: i }))
            .isRequired();
        if (this.state.shouldSendMail)
        {
            this.validator
                .addInput('to', () => this.state.to, i => this.setState({ toError: i }))
                .isRequired();
            this.validator
                .addInput('subject', () => this.state.subject, i => this.setState({ subjectError: i }))
                .isRequired();
            this.validator
                .addInput('message', () => this.strippedMessage, i => this.setState({ messageError: i }))
                .isRequired();
        }
        if (this.state.shouldSendMobilePush)
        {
            this.validator
                .addInput('to', () => this.state.to, i => this.setState({ toError: i }))
                .isRequired();
            this.validator
                .addInput('subject', () => this.state.subject, i => this.setState({ subjectError: i }))
                .isRequired();
            this.validator
                .addInput('message', () => this.strippedMessage, i => this.setState({ messageError: i }))
                .isRequired();
            this.validator
                .addInput('mobilePushTo', () => this.state.mobilePushTo, i => this.setState({ mobilePushToError: i }))
                .isRequired();
            this.validator
                .addInput('mobilePushTitle', () => this.state.mobilePushTitle, i => this.setState({ mobilePushTitleError: i }))
                .isRequired();
            this.validator
                .addInput('mobilePushMessage', () => this.state.mobilePushMessage, i => this.setState({ mobilePushMessageError: i }))
                .isRequired();
            this.validator
                .addInput('notificationArea', () => this.state.mobilePushMessage, i => this.setState({ notificationAreaError: i }))
                .isRequired();
            this.validator
                .addInput('mobilePushBadge', () => this.state.mobilePushBadge, i => this.setState({ mobilePushBadgeError: i }))
                .isRequired();
            this.validator
                .addInput('mobilePushAction', () => this.state.mobilePushAction, i => this.setState({ mobilePushActionError: i }))
                .isRequired();
            this.validator
                .addInput('mobilePushActionUri', () => this.state.mobilePushActionUri, i => this.setState({ mobilePushActionUriError: i }))
                .isRequired();
        }
    }

    private async loadNotification(): Promise<void>
    {
        const notification = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.notificationRules.byId.get<NotificationData>({
            id: this.notificationId.toString(),
            select: NotificationData,
            tenancy: appContext().tenantId,
        });
        const isHtml = RegExp.prototype.test.bind(/(<([^>]+)>)/i);
        const isHtmlValid = isHtml(notification.Message);
        let htmlMessage;

        if (notification.IsHTML && (!isHtmlValid || notification.Message.includes('<style>')))
        {

            const parser = new DOMParser();
            htmlMessage = parser.parseFromString(notification.Message, 'text/html');
            htmlMessage = htmlMessage.body.innerText;
        }
        else
        {
            htmlMessage = notification.Message ?? '';
        }

        await this.setStateAsync({
            name: notification.Name ?? '',
            eventTypeName: notification.EventTypeName ?? '',
            ruleExpression: notification.RuleExpression ?? '',
            disabled: notification.Disabled,
            shouldSendMail: notification.ShouldSendMail,
            shouldSendMobilePush: notification.ShouldSendMobilePush,

            to: notification.To ?? '',
            cc: notification.Cc ?? '',
            bcc: notification.Bcc ?? '',
            replyTo: notification.ReplyTo ?? '',
            subject: notification.Subject ?? '',

            isHtml: notification.IsHTML,
            message: htmlMessage,

            mobilePushTo: notification.MobilePushTo ?? '',
            mobilePushTitle: notification.MobilePushTitle ?? '',
            mobilePushMessage: notification.MobilePushMessage ?? '',
            notificationArea: notification.NotificationArea ?? 'Generic',
            notificationAreaLabel: this.notificationAreas.find(x => x.value === notification.NotificationArea)?.label ?? '',
            mobilePushAction: notification.MobilePushAction ?? '',
            mobilePushBadge: notification.MobilePushBadge ?? '',
            mobilePushActionUri: notification.MobilePushActionUri ?? '',

            attachmentType: notification.AttachmentType,
            shouldSendTwilio: notification.ShouldSendTwilio,
            twilioTo: notification.TwilioTo ?? '',
            twilioMessage: notification.TwilioMessage ?? '',

            concurrencyTimestamp: notification["@odata.etag"]
        });
    }

    private get strippedMessage(): string
    {
        const stripped = this.state.message
            .replace(/<\/?[^>]+>/g, '') // remove HTML tags
            .replace(/&nbsp;/g, '')     // remove non-breaking spaces
            .trim();                    // trim whitespace

        return stripped;
    }

    private async handleDelete(): Promise<void>
    {
        await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.notificationRules.byId.delete({
            id: this.notificationId,
            tenancy: appContext().tenantId,
        });
        this.props.navigate(this.backUrl);
    }

    private async handleSave(): Promise<void>
    {
        if (!this.validator.validateAll())
        {
            return;
        }

        const {
            name, eventTypeName, ruleExpression, disabled, shouldSendMail, shouldSendMobilePush,
            to, replyTo, cc, bcc, subject, isHtml, message,
            mobilePushTo, mobilePushTitle, mobilePushMessage, notificationArea, mobilePushBadge, mobilePushAction, mobilePushActionUri,
            twilioTo, twilioMessage, shouldSendTwilio, attachmentType, concurrencyTimestamp,
        } = this.state;

        const payload: IPostNotificationRule = {
            // common properties
            Name: name,
            EventTypeName: eventTypeName,
            RuleExpression: ruleExpression,
            Disabled: disabled,
            ShouldSendMail: shouldSendMail,
            ShouldSendMobilePush: shouldSendMobilePush,

            // send email properties
            To: (shouldSendMail ? to : ''),
            Cc: (shouldSendMail ? cc : ''),
            Bcc: (shouldSendMail ? bcc : ''),
            ReplyTo: (shouldSendMail ? replyTo : ''),
            Subject: (shouldSendMail ? subject : ''),
            IsHTML: (shouldSendMail ? isHtml : false),
            Message: (shouldSendMail ? message : ''),

            // send mobile push properties
            MobilePushTo: (shouldSendMobilePush ? mobilePushTo : ''),
            MobilePushTitle: (shouldSendMobilePush ? mobilePushTitle : ''),
            MobilePushMessage: (shouldSendMobilePush ? mobilePushMessage : ''),
            NotificationArea: (shouldSendMobilePush ? notificationArea as NotificationAreaEnum ?? 'Generic' : 'Generic'),
            MobilePushAction: (shouldSendMobilePush ? mobilePushAction : ''),
            MobilePushBadge: (shouldSendMobilePush ? mobilePushBadge : ''),
            MobilePushActionUri: (shouldSendMobilePush ? mobilePushActionUri : ''),

            // other
            TwilioTo: twilioTo,
            TwilioMessage: twilioMessage,
            ShouldSendTwilio: shouldSendTwilio,
            AttachmentType: attachmentType as NotificationRuleAttachmentTypeEnum || 'None',
        };
        await this.loading(async () => 
        {
            if (this.isNew)
            {
                await this.createNotification(payload);
            }
            else
            {
                const putPayload: IPutNotificationRule = {
                    ...payload,
                    Name: payload.Name ?? '',
                    ShouldSendMail: payload.ShouldSendMail ?? false,
                    ShouldSendMobilePush: payload.ShouldSendMobilePush ?? false,
                    ShouldSendTwilio: payload.ShouldSendTwilio ?? false,
                    IsHTML: payload.IsHTML ?? false,
                    ConcurrencyStamp: concurrencyTimestamp,
                };
                await this.updateNotification(putPayload);
            }
        });
        this.props.navigate(this.backUrl);
    }

    private async createNotification(payload: IPostNotificationRule): Promise<void>
    {
        await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.notificationRules.post({
            tenancy: appContext().tenantId,
            body: payload
        });
    }

    private async updateNotification(payload: IPutNotificationRule): Promise<void>
    {
        await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.notificationRules.byId.put({
            id: this.notificationId,
            tenancy: appContext().tenantId,
            body: payload
        });
    }

    private handleShouldSendMobilePushChange(value: boolean): void 
    {
        if (value)
        {
            this.validator
                .addInput('mobilePushTo', () => this.state.mobilePushTo, i => this.setState({ mobilePushToError: i }))
                .isRequired();
            this.validator
                .addInput('mobilePushTitle', () => this.state.mobilePushTitle, i => this.setState({ mobilePushTitleError: i }))
                .isRequired();
            this.validator
                .addInput('mobilePushMessage', () => this.state.mobilePushMessage, i => this.setState({ mobilePushMessageError: i }))
                .isRequired();
            this.validator
                .addInput('notificationArea', () => this.state.mobilePushMessage, i => this.setState({ notificationAreaError: i }))
                .isRequired();
            this.validator
                .addInput('mobilePushBadge', () => this.state.mobilePushBadge, i => this.setState({ mobilePushBadgeError: i }))
                .isRequired();
            this.validator
                .addInput('mobilePushAction', () => this.state.mobilePushAction, i => this.setState({ mobilePushActionError: i }))
                .isRequired();
            this.validator
                .addInput('mobilePushActionUri', () => this.state.mobilePushActionUri, i => this.setState({ mobilePushActionUriError: i }))
                .isRequired();
        }
        else
        {
            this.validator
                .removeInput('mobilePushTo')
                .removeInput('mobilePushTitle')
                .removeInput('mobilePushMessage')
                .removeInput('notificationArea')
                .removeInput('mobilePushBadge')
                .removeInput('mobilePushAction')
                .removeInput('mobilePushActionUri');
        }
        this.setState({ shouldSendMobilePush: value }, () => this.validator.validate('shouldSendMobilePush'));
    }

    private handleShouldSendEmailChange(value: boolean): void 
    {
        if (value)
        {
            this.validator
                .addInput('to', () => this.state.to, i => this.setState({ toError: i }))
                .isRequired();
            this.validator
                .addInput('subject', () => this.state.subject, i => this.setState({ subjectError: i }))
                .isRequired();
            this.validator
                .addInput('message', () => this.strippedMessage, i => this.setState({ messageError: i }))
                .isRequired();
        }
        else
        {
            this.validator
                .removeInput('to')
                .removeInput('subject')
                .removeInput('message');
        }
        this.setState({ shouldSendMail: value }, () => this.validator.validate('shouldSendMail'));
    }

    private renderCommonProperties(): JSX.Element
    {
        return (
            <Card
                title={this.labels.Platform_Notification_DetailsTitle}
                subTitle={this.labels.Platform_Notification_DetailsSubTitle}
                onSectionUpdate={i => this.setState(prev => ({ sections: PageNav.update(prev.sections, i, 0) }))}
                icon={<SerendieSymbol name='bell' />}
            >
                <GridContainer>
                    <GridItem expanded={6}>
                        <TextField
                            required
                            label={this.labels.Notification_Name}
                            placeholder={this.labels.Notification_Name}
                            value={this.state.name}
                            onChange={e => this.setState({ name: e.target.value }, () => this.validator.validate('name'))}
                            invalid={this.state.nameError !== ''}
                            invalidMessage={this.state.nameError}
                        />
                    </GridItem>
                    <GridItem expanded={6}>
                        <TextField
                            required
                            label={this.labels.Notification_EventTypeName}
                            placeholder={this.labels.Notification_EventTypeName}
                            value={this.state.eventTypeName}
                            onChange={e => this.setState({ eventTypeName: e.target.value }, () => this.validator.validate('eventTypeName'))}
                            invalid={this.state.eventTypeError !== ''}
                            invalidMessage={this.state.eventTypeError}
                        />
                    </GridItem>
                    <GridItem>
                        <TextField
                            required
                            label={this.labels.Notification_RuleExpression}
                            placeholder={this.labels.Notification_RuleExpression}
                            value={this.state.ruleExpression}
                            onChange={e => this.setState({ ruleExpression: e.target.value }, () => this.validator.validate('ruleExpression'))}
                            invalid={this.state.ruleExpressionError !== ''}
                            invalidMessage={this.state.ruleExpressionError}
                        />
                    </GridItem>
                    <GridItem>
                        <Switch
                            label={this.labels.Notification_Disabled}
                            checked={this.state.disabled}
                            onCheckedChange={e => this.setState({ disabled: e.checked as boolean })}
                        />
                    </GridItem>
                    <GridItem>
                        <Switch
                            label={this.labels.Notification_ShouldSendEmail}
                            checked={this.state.shouldSendMail}
                            onCheckedChange={e => this.handleShouldSendEmailChange(e.checked)}
                        />
                    </GridItem>
                    <GridItem>
                        <Switch
                            label={this.labels.Notification_ShouldSendMobilePush}
                            checked={this.state.shouldSendMobilePush}
                            onCheckedChange={e => this.handleShouldSendMobilePushChange(e.checked)}
                        />
                    </GridItem>
                </GridContainer>
            </Card>
        );
    }

    private renderEmailProperties(): JSX.Element
    {
        return (
            <Card
                title={this.labels.Platform_Notification_SendEmailDetailsTitle}
                subTitle={this.labels.Platform_Notification_SendEmailDetailsSubTitle}
                onSectionUpdate={i => this.setState(prev => ({ sections: PageNav.update(prev.sections, i, 1) }))}
            >
                <GridContainer>
                    <GridItem expanded={6}>
                        <TextField
                            label={this.labels.Notification_To}
                            value={this.state.to}
                            required
                            placeholder={this.labels.Notification_To}
                            onChange={e => this.setState({ to: e.target.value }, () => this.validator.validate('to'))}
                            invalid={!!this.state.toError}
                            invalidMessage={this.state.toError}
                        />
                    </GridItem>
                    <GridItem expanded={6}>
                        <TextField
                            label={this.labels.Notification_ReplyTo}
                            value={this.state.replyTo}
                            placeholder={this.labels.Notification_ReplyTo}
                            onChange={e => this.setState({ replyTo: e.target.value }, () => this.validator.validate('replyTo'))}
                        />
                    </GridItem>
                    <GridItem expanded={6}>
                        <TextField
                            label={this.labels.Notification_Cc}
                            value={this.state.cc}
                            placeholder={this.labels.Notification_Cc}
                            onChange={e => this.setState({ cc: e.target.value }, () => this.validator.validate('cc'))}
                        />
                    </GridItem>
                    <GridItem expanded={6}>
                        <TextField
                            label={this.labels.Notification_Bcc}
                            value={this.state.bcc}
                            placeholder={this.labels.Notification_Bcc}
                            onChange={e => this.setState({ bcc: e.target.value }, () => this.validator.validate('bcc'))}
                        />
                    </GridItem>
                    <GridItem>
                        <TextField
                            label={this.labels.Notification_Subject}
                            value={this.state.subject}
                            required
                            placeholder={this.labels.Notification_Subject}
                            onChange={e => this.setState({ subject: e.target.value }, () => this.validator.validate('subject'))}
                            invalid={!!this.state.subjectError}
                            invalidMessage={this.state.subjectError}
                        />
                    </GridItem>
                    <GridItem>
                        <Switch
                            label={this.labels.Notification_IsHTML}
                            checked={this.state.isHtml}
                            onCheckedChange={e => this.setState({ isHtml: e.checked as boolean }, () => this.validator.validate('isHtml'))}
                        />
                    </GridItem>
                    <GridItem>
                        {this.state.isHtml ?
                            <>
                                <JoditEditor
                                    value={this.state.message}
                                    onBlur={e => this.setState({ message: e }, () => this.validator.validate('message'))}
                                    config={{
                                        showCharsCounter: false,
                                        showWordsCounter: false,
                                        showXPathInStatusbar: false,
                                        zIndex: 0,
                                        readonly: false,
                                        activeButtonsInReadOnly: ['source', 'fullsize', 'print', 'about', 'dots'],
                                        toolbarButtonSize: 'middle',
                                        theme: 'default',
                                        saveModeInCookie: false,
                                        spellcheck: true,
                                        editorCssClass: true,
                                        triggerChangeEvent: true,
                                        width: 'auto',
                                        height: 'auto',
                                        minHeight: 100,
                                        direction: '',
                                        language: 'auto',
                                        debugLanguage: false,
                                        // @ts-ignore
                                        i18n: 'en',
                                        tabIndex: -1,
                                        toolbar: true,
                                        enter: "p",
                                        defaultMode: 1,
                                        useSplitMode: false,
                                        askBeforePasteHTML: true,
                                        askBeforePasteFromWord: true,
                                    }}
                                />
                                {!!this.state.messageError && <HelperText error>{this.state.messageError}</HelperText>}
                            </>
                            :
                            <TextArea
                                label={this.labels.Notification_Message}
                                placeholder={this.labels.Notification_Message}
                                rows={15}
                                required
                                value={this.state.message}
                                onChange={e => this.setState({ message: e.target.value }, () => this.validator.validate('message'))}
                                invalid={!!this.state.messageError}
                                invalidMessage={this.state.messageError}
                            />
                        }
                    </GridItem>

                </GridContainer>
            </Card>

        );
    }

    private renderMobilePushProperties(): JSX.Element
    {
        return (
            <Card
                title={this.labels.Platform_Notification_SendPushDetailsTitle}
                subTitle={this.labels.Platform_Notification_SendPushDetailsSubTitle}
                onSectionUpdate={i => this.setState(prev => ({ sections: PageNav.update(prev.sections, i, 2) }))}
            >
                <GridContainer>
                    <GridItem expanded={6}>
                        <TextField
                            label={this.labels.Notification_MobilePushTo}
                            placeholder={this.labels.Notification_MobilePushTo}
                            value={this.state.mobilePushTo}
                            required
                            onChange={e => this.setState({ mobilePushTo: e.target.value }, () => this.validator.validate('mobilePushTo'))}
                            invalid={!!this.state.mobilePushToError}
                            invalidMessage={this.state.mobilePushToError}
                        />
                    </GridItem>
                    <GridItem expanded={6}>
                        <TextField
                            label={this.labels.Notification_MobilePushTitle}
                            placeholder={this.labels.Notification_MobilePushTitle}
                            value={this.state.mobilePushTitle}
                            required
                            onChange={e => this.setState({ mobilePushTitle: e.target.value }, () => this.validator.validate('mobilePushTitle'))}
                            invalid={!!this.state.mobilePushTitleError}
                            invalidMessage={this.state.mobilePushTitleError}
                        />
                    </GridItem>
                    <GridItem>
                        <TextField
                            label={this.labels.Notification_MobilePushMessage}
                            placeholder={this.labels.Notification_MobilePushMessage}
                            value={this.state.mobilePushMessage}
                            required
                            onChange={e => this.setState({ mobilePushMessage: e.target.value }, () => this.validator.validate('mobilePushMessage'))}
                            invalid={!!this.state.mobilePushMessageError}
                            invalidMessage={this.state.mobilePushMessageError}
                        />
                    </GridItem>
                    <GridItem expanded={6}>
                        <Select
                            label={this.labels.Notification_NotificationArea}
                            placeholder={this.state.notificationAreaLabel}
                            items={this.notificationAreas.map(x => ({ label: x.label, value: x.value.toString() }))}
                            required
                            onSelect={(e) => this.setState({ notificationArea: e.value }, () => this.validator.validate('notificationArea'))}
                            invalid={!!this.state.notificationAreaError}
                            invalidMessage={this.state.notificationAreaError}
                        />
                    </GridItem>
                    <GridItem expanded={6}>
                        <TextField
                            label={this.labels.Notification_MobilePushBadge}
                            placeholder={this.labels.Notification_MobilePushBadge}
                            value={this.state.mobilePushBadge}
                            required
                            onChange={e => this.setState({ mobilePushBadge: e.target.value }, () => this.validator.validate('mobilePushBadge'))}
                            invalid={!!this.state.mobilePushBadgeError}
                            invalidMessage={this.state.mobilePushBadgeError}
                        />
                    </GridItem>
                    <GridItem expanded={6}>
                        <TextField
                            label={this.labels.Notification_MobilePushAction}
                            placeholder={this.labels.Notification_MobilePushAction}
                            value={this.state.mobilePushAction}
                            required
                            onChange={e => this.setState({ mobilePushAction: e.target.value }, () => this.validator.validate('mobilePushAction'))}
                            invalid={!!this.state.mobilePushActionError}
                            invalidMessage={this.state.mobilePushActionError}
                        />
                    </GridItem>
                    <GridItem expanded={6}>
                        <TextField
                            label={this.labels.Notification_MobilePushActionUri}
                            placeholder={this.labels.Notification_MobilePushActionUri}
                            value={this.state.mobilePushActionUri}
                            required
                            onChange={e => this.setState({ mobilePushActionUri: e.target.value }, () => this.validator.validate('mobilePushActionUri'))}
                            invalid={!!this.state.mobilePushActionUriError}
                            invalidMessage={this.state.mobilePushActionUriError}
                        />
                    </GridItem>
                </GridContainer>
            </Card>
        );
    }

    public render(): JSX.Element
    {
        return (
            <LayoutContainer>
                <LayoutContent>
                    <GridContainer spacing="cards">
                        <GridItem>
                            {this.renderCommonProperties()}
                        </GridItem>
                        {
                            this.state.shouldSendMail &&
                            <GridItem>
                                {this.renderEmailProperties()}
                            </GridItem>
                        }
                        {
                            this.state.shouldSendMobilePush &&
                            <GridItem>
                                {this.renderMobilePushProperties()}
                            </GridItem>
                        }
                    </GridContainer>
                </LayoutContent>
                <LayoutRight>
                    <PageNav
                        sections={this.state.sections}
                    />
                </LayoutRight>
                <LayoutFooter>
                    <ButtonBar>
                        <LeftButtons>
                            {
                                !this.isNew &&
                                <DeleteButton
                                    disabled={!this.userCanDelete}
                                    dialogMessage={this.labels.Platform_Notification_ConfirmDeleteMsg}
                                    onDelete={() => this.handleDelete()}
                                />
                            }
                        </LeftButtons>
                        <RightButtons>
                            <Button
                                styleType='outlined'
                                onClick={() => this.props.navigate(this.backUrl)}
                            >
                                {this.labels.Platform_CommonAction_Cancel}
                            </Button>
                            <Button
                                styleType='filled'
                                disabled={!this.userCanUpdate || !this.validator.isValid}
                                onClick={() => this.handleSave()}
                            >
                                {this.labels.Platform_CommonAction_Save}
                            </Button>
                        </RightButtons>
                    </ButtonBar>
                </LayoutFooter>
            </LayoutContainer>
        );
    }
}

export default withRouting(EditNotification);

export interface IProps extends IRouterProps<IMatchParams>
{
}

export interface IMatchParams
{
    notificationid: string | undefined;
}

export interface IState
{
    name: string;
    nameError: string;
    eventTypeName: string;
    eventTypeError: string;
    ruleExpression: string;
    ruleExpressionError: string;
    disabled: boolean;
    shouldSendMail: boolean;
    shouldSendMobilePush: boolean;

    to: string;
    toError: string;
    replyTo: string;
    cc: string;
    bcc: string;
    subject: string;
    subjectError: string;
    isHtml: boolean;
    message: string;
    messageError: string;

    mobilePushTo: string;
    mobilePushToError: string;
    mobilePushTitle: string;
    mobilePushTitleError: string;
    mobilePushMessage: string;
    mobilePushMessageError: string;
    notificationArea: string;
    notificationAreaLabel: string;
    notificationAreaError: string;
    mobilePushBadge: string;
    mobilePushBadgeError: string;
    mobilePushAction: string;
    mobilePushActionError: string;
    mobilePushActionUri: string;
    mobilePushActionUriError: string;

    attachmentType: string;
    shouldSendTwilio: boolean;
    twilioTo: string;
    twilioMessage: string;

    concurrencyTimestamp: string;

    sections: IPageNavSection[];
}
